/**
 * Citation Traceability — canonical types + adapter contracts.
 *
 * Pack: Citation_Traceability_Architecture (CT1, Stage 1).
 * Master plan row: 0.99152.
 *
 * ─── Purpose ──────────────────────────────────────────────────────────
 *
 * Every numeric/categorical claim a Datacrate report makes is sourced
 * from somewhere: an uploaded document, a third-party provider, a user
 * amendment, a site-context claim, a regional baseline, or a derivation
 * of any of the above. Today the provenance lives in five SCATTERED
 * shapes:
 *
 *   - SiteDocument                (src/types/siteRag.ts)
 *   - EvidenceSection<T>          (src/types/siteEvidence.ts)
 *   - SEDAmendment                (src/types/sedAmendments.ts)
 *   - ExtractedClaim/SiteContext  (src/types/siteContext.ts)
 *   - Tier-3 baseline registry    (src/services/evidenceAssist/substituteRegistry.ts)
 *
 * No single UI surface answers the user's question "where did this
 * number come from?" The header on `DocChecklistPanel.tsx:257-273` will
 * cheerfully tell the user "18/18 auto-satisfied" without ever exposing
 * which 18 rows, what backing data, or what confidence.
 *
 * `EvidenceSource` (below) is the canonical union — one variant per
 * primitive — into which every existing shape PROJECTS via a pure
 * adapter. The storage layer is untouched; the union sits at the
 * boundary so downstream UI, narrative composition, and admin tooling
 * read ONE type instead of five.
 *
 * ─── Foundation discipline ────────────────────────────────────────────
 *
 * • The union is discriminated on `kind`. Callers narrow with
 *   `Extract<EvidenceSource, {kind: '…'}>`. Never widen by `as`.
 * • Adapters are pure functions. No Firestore, no Gemini, no I/O.
 * • Nothing in this file mutates source data. The hook
 *   (`useEvidenceLineage`, CT2) likewise reads only.
 * • Adding a new evidence primitive means adding a `kind` variant here
 *   AND its adapter, and updating every `switch` exhaustively. The
 *   `assertNeverSource` helper enforces this at compile time.
 *
 * ─── Stage roadmap (informational) ────────────────────────────────────
 *
 *   Stage 1 (this pack)  CT1 types + adapters · CT2 read hook
 *   Stage 2              User-facing surfaces (variable/section panels,
 *                        checklist row provenance, claim tooltips)
 *   Stage 3              Wire into-report citations + DOCX/PDF export
 *   Stage 4              Admin invariants + cross-pack convergence
 *
 * Downstream packs (DM/SA/LB3/UD/RC) project into the union via these
 * adapters. None of them re-import their own scattered shapes through
 * `EvidenceSource` — that is the Convergence Pass in a later stage.
 */

import type { SiteDocument } from './siteRag';
import type { SEDAmendment, AmendmentSource } from './sedAmendments';
import type { ExtractedClaim } from './siteContext';

/* ── Source-confidence helpers ───────────────────────────────────────── */

/**
 * A 0..1 confidence score. Out-of-range and non-finite inputs are
 * clamped — never NaN propagates downstream. Used by both `confidence`
 * (on `EvidenceLineage`) and by every adapter that derives a
 * normalised score from a provider-native 0..100 / boolean / etc.
 */
export function clampConfidence(x: number | undefined | null): number {
  if (x == null || !Number.isFinite(x)) return 0;
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}

/* ── The canonical union ─────────────────────────────────────────────── */

/**
 * The five primitive citation kinds plus a derived bucket. Every
 * `EvidenceLineage.sources` entry is one of these.
 *
 * Variant invariants:
 *
 * - `document` — uploaded SiteDocument. `quote` is the verbatim
 *   snippet the renderer can show inline (truncated upstream).
 *   `relevanceScore` is 0..1 (per-report-type relevance from
 *   `relevanceClassifier`); absent for legacy docs.
 *
 * - `provider` — third-party data provider (EIA, NREL, FEMA, …).
 *   `providerKey` is the canonical id from the provider catalogue
 *   (e.g. `'eia'`, `'nrel-wind'`, `'fema-flood'`). `value` is the
 *   raw datum the provider returned (unknown intentionally — each
 *   section narrows it). `capturedAt` is the ISO timestamp from
 *   `EvidenceSection.retrievedAt`.
 *
 * - `user-amendment` — a `SEDAmendment` row. `amendmentId` is the
 *   Firestore doc id; the renderer reads the full amendment by id
 *   when it needs the value / reason / reviewer.
 *
 * - `site-context-claim` — a user-declared claim extracted from the
 *   freeform site-context prose. `claimId` is the `ExtractedClaim.id`.
 *   `field` is the SED dot-path the claim maps to (e.g.
 *   `'grid.data.substationDistKm'`).
 *
 * - `regional-baseline` — an entry from the public-baseline registry
 *   (Tier-3 substitute). `baselineKey` is the substitute-registry id
 *   (typically the checklist row id, e.g. `'phase1-esa'`).
 *   `credibility` is the registry's 0..1 credibilityScore (normalised
 *   from the registry's 0..100 by the adapter).
 *
 * - `derived` — a value computed from other sources. `from` is the
 *   list of source-ids the derivation read (free-form so the union
 *   doesn't pull in every consumer's keying scheme). `rationale` is
 *   a one-sentence "how we got here" string.
 */
export type EvidenceSource =
  | { kind: 'document'; docId: string; quote?: string; relevanceScore?: number }
  | { kind: 'provider'; providerKey: string; value: unknown; capturedAt: string }
  | { kind: 'user-amendment'; amendmentId: string; userId: string; amendedAt: string; note?: string }
  | { kind: 'site-context-claim'; claimId: string; field: string; capturedAt: string }
  | { kind: 'regional-baseline'; baselineKey: string; credibility: number }
  | { kind: 'derived'; from: string[]; rationale?: string };

/**
 * The discriminator literal type — useful for switch coverage tests
 * and admin filtering UIs.
 */
export type EvidenceSourceKind = EvidenceSource['kind'];

/**
 * Exhaustiveness helper. Use in a `switch (source.kind)` default branch
 * to make TypeScript enforce that every variant is handled. A new
 * variant added to the union without updating the switch is a compile
 * error, not a runtime surprise.
 */
export function assertNeverSource(x: never): never {
  throw new Error(`Unhandled EvidenceSource kind: ${JSON.stringify(x)}`);
}

/* ── Lineage target ──────────────────────────────────────────────────── */

/**
 * What the lineage is FOR. The hook resolves a `LineageTarget` and
 * returns the `EvidenceLineage` for it. The four kinds cover every
 * surface Stage 2 will render:
 *
 *  - `'sed-variable'`   — a leaf field on the SED (e.g. seismic.pga).
 *  - `'sed-section'`    — a section on the SED (e.g. seismic).
 *  - `'report-claim'`   — an in-report inline citation `[Ref N]`.
 *  - `'checklist-row'`  — a row on the DocChecklistPanel.
 *
 * `id` shape is target-kind-specific:
 *  - sed-variable      → dot-path matching `SEDAmendment.variablePath`
 *    (e.g. `'seismic.data.pga'`).
 *  - sed-section       → `SedSectionId` (e.g. `'seismic'`).
 *  - report-claim      → the report's own claim id; today that's the
 *    `[Ref N]` token's index-as-string. Stage 3 widens this when
 *    inline claim ids land.
 *  - checklist-row     → `ChecklistItem.id` (e.g. `'phase1-esa'`).
 */
export interface LineageTarget {
  kind: 'sed-variable' | 'sed-section' | 'report-claim' | 'checklist-row';
  id: string;
}

/* ── EvidenceLineage ─────────────────────────────────────────────────── */

/**
 * The unified read shape returned by `useEvidenceLineage`. The same
 * shape feeds every Stage-2 surface (variable browser, section editor,
 * checklist provenance, claim tooltips) and every Stage-3 export path
 * (DOCX, PDF, JSON snapshot).
 *
 * Invariants:
 *  - `sources` is always an array — never undefined. Empty array means
 *    "no provenance on record"; consumers render the gap chip.
 *  - `confidence` is 0..1, blended from per-source confidence and
 *    content-confidence (Stage 2 picks the blending rule; Stage 1
 *    stores the field). Computed at READ time; never persisted.
 *  - `userOverride`, when present, replaces `newValue` semantically
 *    but does NOT erase `sources` — Stage 2 surfaces both
 *    "what the user overrode" and "what the original sources said".
 */
export interface EvidenceLineage {
  target: LineageTarget;
  sources: EvidenceSource[];
  /** 0..1 — blended source/content confidence. Computed at read. */
  confidence: number;
  userOverride?: {
    newValue: unknown;
    reason: string;
    overriddenAt: string;
    overriddenBy: string;
  };
  /**
   * Multi-Parcel Site Boundary (MP pack) — Phase 2 cross-pack scaffold.
   *
   * Optional id of the parcel this lineage is scoped to. References
   * `SiteEvidenceDocument.parcels[].id` (MP4). When set, the lineage
   * applies to a single parcel rather than the whole site (e.g. "FEMA
   * flood zone X for the main parcel only" when an adjacent parcel sits
   * in zone AE).
   *
   * Absent = lineage applies site-wide (legacy single-parcel site or a
   * source that doesn't vary by parcel). No runtime logic is wired
   * against this field yet — Stage-2 surfaces (variable browser,
   * section editor) gain parcel-aware UI in MP Phase 3.
   */
  parcelId?: string;
}

/**
 * The empty/default lineage. Returned by the hook when nothing on
 * disk pertains to the target. The renderer keys "no provenance" on
 * `sources.length === 0` (not on `=== emptyLineage`) so a partial
 * projection that found one source still renders normally.
 */
export function emptyLineage(target: LineageTarget): EvidenceLineage {
  return { target, sources: [], confidence: 0 };
}

/* ── Adapters ────────────────────────────────────────────────────────────
 *
 * One adapter per `EvidenceSource` variant. Each is a pure function
 * with a tight Extract<…> return type so callers stay narrowed without
 * casting.
 * ────────────────────────────────────────────────────────────────────── */

/**
 * Project a `SiteDocument` into a `'document'` source.
 *
 * @param doc            The uploaded document.
 * @param quote          Optional verbatim snippet the renderer shows.
 *                       Truncated/sanitised UPSTREAM — the adapter
 *                       passes it through verbatim so this stays pure.
 * @param relevanceScore Optional 0..1 score (per-report-type relevance
 *                       from `relevanceClassifier`). Clamped via
 *                       `clampConfidence`. Pass the doc's relevance
 *                       for the active report type, NOT the average.
 */
export function documentToSource(
  doc: Pick<SiteDocument, 'id'>,
  quote?: string,
  relevanceScore?: number,
): Extract<EvidenceSource, { kind: 'document' }> {
  const out: Extract<EvidenceSource, { kind: 'document' }> = {
    kind: 'document',
    docId: doc.id,
  };
  if (quote != null) out.quote = quote;
  if (relevanceScore != null) out.relevanceScore = clampConfidence(relevanceScore);
  return out;
}

/**
 * Project a provider datum into a `'provider'` source.
 *
 * @param providerKey Canonical provider id (e.g. `'eia'`).
 * @param value       The raw datum (unknown by design — variants
 *                    declare their own narrowing).
 * @param capturedAt  ISO timestamp from `EvidenceSection.retrievedAt`.
 *                    Must be a parseable ISO string; the adapter does
 *                    not validate — that's enforced at the section
 *                    level. Passing a non-ISO string is a bug at the
 *                    call site.
 */
export function providerToSource(
  providerKey: string,
  value: unknown,
  capturedAt: string,
): Extract<EvidenceSource, { kind: 'provider' }> {
  return { kind: 'provider', providerKey, value, capturedAt };
}

/**
 * Project a `SEDAmendment` into a `'user-amendment'` source.
 *
 * Reads only the fields strictly needed for citation — the full
 * amendment is fetched by id when the user opens the detail panel.
 * `note` is the user's `reason` (renamed at the boundary so the union
 * stays UI-neutral; the amendment side uses `reason` which is
 * required-on-write, while the union's `note` is optional because
 * Stage-2 UIs may surface or hide it).
 */
export function amendmentToSource(
  amendment: Pick<SEDAmendment, 'id' | 'createdBy' | 'createdAt' | 'reason'>,
): Extract<EvidenceSource, { kind: 'user-amendment' }> {
  const out: Extract<EvidenceSource, { kind: 'user-amendment' }> = {
    kind: 'user-amendment',
    amendmentId: amendment.id,
    userId: amendment.createdBy,
    amendedAt: amendment.createdAt,
  };
  if (amendment.reason) out.note = amendment.reason;
  return out;
}

/**
 * Project an `ExtractedClaim` into a `'site-context-claim'` source.
 *
 * The claim's `sedField` is optional on the source shape (claims can
 * be `'unverifiable'` and carry no field). The adapter requires the
 * caller to pass an explicit `field` so the union always knows which
 * SED slot the claim binds to — when the claim is unverifiable, the
 * caller should NOT project it; surface it through Stage-2's
 * "context-only" path instead.
 *
 * @param claim       The extracted claim (we read its `id` only).
 * @param field       The SED dot-path the claim cites. Must be
 *                    non-empty.
 * @param capturedAt  ISO timestamp the claim was extracted at. Falls
 *                    through to the `EvidenceLineage` so Stage-2 can
 *                    sort claims by recency.
 */
export function siteContextClaimToSource(
  claim: Pick<ExtractedClaim, 'id'>,
  field: string,
  capturedAt: string,
): Extract<EvidenceSource, { kind: 'site-context-claim' }> {
  if (!field) {
    throw new Error('siteContextClaimToSource: `field` is required and must be non-empty.');
  }
  return { kind: 'site-context-claim', claimId: claim.id, field, capturedAt };
}

/**
 * Project a regional-baseline registry entry into a
 * `'regional-baseline'` source.
 *
 * @param rowId         Substitute-registry id (typically the checklist
 *                      row id, e.g. `'phase1-esa'`).
 * @param credibility   Credibility score. Accepts both the registry's
 *                      native 0..100 and the canonical 0..1 — values
 *                      strictly > 1 are treated as 0..100 and divided
 *                      by 100 before clamping. The adapter normalises
 *                      because callers cross the boundary with both
 *                      conventions on disk (the registry stores 0..100;
 *                      newer config blobs store 0..1).
 */
export function regionalBaselineToSource(
  rowId: string,
  credibility: number,
): Extract<EvidenceSource, { kind: 'regional-baseline' }> {
  const normalised = credibility > 1 ? credibility / 100 : credibility;
  return { kind: 'regional-baseline', baselineKey: rowId, credibility: clampConfidence(normalised) };
}

/* ── In-report claims (CT6) ──────────────────────────────────────────── */

/**
 * A single claim emitted by the LLM in the structured `claims[]` field
 * (CT6 schema bump). Each entry is one sentence the model wrote into
 * the narrative, paired with the lineage-target refs that back it.
 *
 * `sources` uses a compact string-ref syntax so the schema stays flat
 * (Gemini structured-output allows nested arrays of objects, but
 * primitive arrays are far more robust to the model's preferred shape
 * and round-trip through JSON.stringify without surprises):
 *
 *   'ref:N'             — 1-based index into report.references[]
 *                         (matches the [Ref N] token discipline)
 *   'doc:<docId>'       — uploaded document by stable id
 *   'sed:<path>'        — SED variable by dot-path (e.g. 'site.acres')
 *   'sed-section:<id>'  — SED section by id
 *   'provider:<key>'    — provider call by canonical key
 *   'site-context:<id>' — Site Context claim by id
 *   'baseline:<key>'    — regional-baseline registry entry
 *   'bopSim:<id>'       — historic retired simulation evidence (render-only; never generated)
 *   'bopAnnual:<id>'    — historic annual-yield evidence id (compatibility)
 *   'bopLcoe:<id>'      — historic LCOE evidence id (compatibility)
 *   'annualYield:<id>'  — legacy/reserved annual-yield evidence id
 *   'financeLcoe:<id>'  — legacy/reserved finance LCOE evidence id
 *   'pathwayComparison:<id>' — pinned pathway comparison decision artifact (R-131-FIN-DECISION-K)
 *
 * The resolver (Stage 3 hook + CT2 lineage hook) parses each ref and
 * maps it back to an `EvidenceSource`. Unknown / malformed refs render
 * as orphan markers — the CT9 admin dashboard counts them.
 *
 * `text` is the sentence verbatim (no `[Ref N]` rewriting). It MAY
 * contain reliability tokens (`[verified Ref 3]`) since CT6 composes
 * with the RV reliability pass — the renderer parses them in order:
 * first walkClaimText for badges + refs, then the CT6 wrapper layer
 * appends the per-sentence superscript marker.
 */
export interface ReportClaim {
  /** The sentence text. Should not exceed one sentence; if the model
   *  emits a multi-sentence chunk the density splitter treats it as
   *  one sentence (back-compat). */
  text: string;
  /** Compact lineage-target refs. May be empty when the model has no
   *  source — those entries flow through but inflate the "uncited"
   *  count for the density KPI. */
  sources: string[];
  /** Optional: the section id this claim belongs to. Lets the renderer
   *  scope marker tooltips to the same section's lineage. Free-form
   *  string so per-type schemas can pick their own id space. */
  sectionId?: string;

  // ── CSE (Claims Structured Emission, pack code CSE) ────────────────
  // Optional structured-claim metadata, populated when
  // `extractReportClaims` runs as a post-narrative-gen pass. All
  // fields stay optional so legacy CT6 per-sentence claims (which
  // carry only `text` + `sources` + `sectionId`) continue to render
  // unchanged. RDX-1 / RCO Path B / EVL Tier 1 read these fields off
  // `report.claims[]` when they are present.

  /** Stable claim id. Caller-assigned; `${reportId}.${section}.${field}.${idx}`. */
  id?: string;
  /** Owning report. Allows the merge resolver to score claim sources
   *  by report recency without a containing-report lookup. */
  reportId?: string;
  /** Canonical SED section id this claim belongs to. Mirrors
   *  `sectionId` but is the explicit CSE-named field; RDX-1 walks
   *  this. */
  reportSectionId?: string;
  /** Canonical SED path the claim asserts a value for (e.g.
   *  'grid.capacityMW'). Same dot-path convention as
   *  REPORT_TO_SED_MAPPING entries. */
  field?: string;
  /** Extracted value. Free-form so per-field schemas can carry the
   *  raw shape — numeric MW, string verdict, structured object. */
  value?: unknown;
  /** Caller-emitted confidence on the extracted value (0..1).
   *  Capped at 0.7 by the RDX-4 merge resolver before comparison
   *  against authored evidence. */
  confidence?: number;
  /** UNIX-epoch-ms timestamp set at extraction time. Used by RDX-1
   *  to recency-sort competing report-derived claims. */
  emittedAt?: number;

  /** Explicit provenance marker, stamped ONLY when a claim is merged from the
   *  optional editorial re-voicing layer: `editorial:<sectionId>` names the
   *  editorial layer that emitted it. Never inferred from `sectionId` or array
   *  position; lets a deterministic rollback remove exactly those claims. A
   *  single flat string so the serialized claim row stays one line. */
  origin?: EditorialClaimOrigin;
}

/** Provenance value of a claim emitted by the editorial layer of one section. */
export type EditorialClaimOrigin = `editorial:${string}`;

/**
 * Recognised prefixes for a `ReportClaim.sources` entry. The renderer
 * routes each prefix to the right resolver; unknown prefixes are
 * orphan claims.
 *
 * CSE (Claims Structured Emission) added three lineage-target
 * primitives that the extractor resolves [Ref N] superscripts against:
 *   'kmz:<layerId>'    — KMZ overlay layer
 *   'report:<reportId>' — prior report on the same site (EVL Tier 1)
 *   'dam:<entryId>'     — Datacrate Model entry
 *
 * Historic reports may still contain `bopSim:`, `bopAnnual:`, `bopLcoe:`,
 * `annualYield:`, and `financeLcoe:`. Current generation does not emit the
 * derivative identities while annual/LCOE capability cells remain preview.
 *   'pathwayComparison:<comparisonId>' — pinned pathway comparison (R-131-FIN-DECISION-K).
 * EO/DAE engine rollout (eo-production-rollout.md, surfaced when VITE_EO_ENGINE=on) uses:
 *   'eoSim:<inputHash>#<resultSignature>' — equation-oriented sim evidence anchor. The id is the
 *     run-pinned, reproducible solve signature (see eoReportSection.ts), so an EO number can never be
 *     cited un-provenanced. The payload carries a '#' (hash#hash); parseClaimSource keeps it intact.
 */
export const CLAIM_SOURCE_PREFIXES = [
  'ref:',
  'doc:',
  'sed:',
  'sed-section:',
  'provider:',
  'site-context:',
  'baseline:',
  'bopSim:',
  'bopAnnual:',
  'bopLcoe:',
  'annualYield:',
  'financeLcoe:',
  'eoSim:',
  'pathwayComparison:',
  'kmz:',
  'report:',
  'dam:',
] as const;

export type ClaimSourcePrefix = typeof CLAIM_SOURCE_PREFIXES[number];

/**
 * Best-effort parse of a `ReportClaim.sources` entry into its prefix
 * + payload. Returns null on a malformed entry. Used by the renderer
 * to dispatch to the correct lineage resolver.
 */
export interface ParsedClaimSource {
  prefix: ClaimSourcePrefix;
  payload: string;
}

export function parseClaimSource(raw: string): ParsedClaimSource | null {
  if (!raw || typeof raw !== 'string') return null;
  // Match longest prefix first so 'sed-section:' beats 'sed:'.
  const ordered = [...CLAIM_SOURCE_PREFIXES].sort((a, b) => b.length - a.length);
  for (const p of ordered) {
    if (raw.startsWith(p)) {
      const payload = raw.slice(p.length).trim();
      if (!payload) return null;
      return { prefix: p, payload };
    }
  }
  return null;
}

/* ── Convenience: AmendmentSource → EvidenceSource ────────────────────
 *
 * The amendment system has its own nested `AmendmentSource` carrying
 * the *backing* evidence the user pointed at when they amended. When
 * that backing evidence is a document, Stage-2 wants BOTH the
 * `user-amendment` source AND a `document` source (the doc the user
 * cited). This helper produces the secondary `document` source so
 * the projection stays in one place.
 *
 * Returns `null` for non-document amendment sources (external link,
 * expert judgment, site visit) — those are surfaced through the
 * `user-amendment.note` field at the renderer.
 */
export function amendmentBackingDocumentSource(
  source: AmendmentSource,
): Extract<EvidenceSource, { kind: 'document' }> | null {
  if (source.type !== 'document' || !source.documentId) return null;
  return { kind: 'document', docId: source.documentId };
}
